import React from 'react';
import { conformToMask } from 'react-text-mask';
import { parseISO, format as dateFnsFormat, differenceInDays } from 'date-fns';
import { es, ptBR, enUS, de } from 'date-fns/locale'
import i18n from './i18n';
import accents from 'remove-accents';

const locales = { es, pt: ptBR, en: enUS, de }
const localeWithCountry = { es: 'es-MX', pt: 'pt-BR', en: 'en-US', de: 'de-DE' }

export const phoneMask = [/[1-9]/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/];

export function maskPhone(phone) {
  const conformedPhoneNumber = conformToMask(
    phone,
    phoneMask,
    {guide: false}
  )
  
  return conformedPhoneNumber.conformedValue
}

export function unmaskPhone(phone = '') {
  const regex = /[() \-_]/g
  return phone?.replace(regex, '') || '';
}

export function getDisplayName(name = '', lastName = '') {
  if (!lastName || !lastName.length) return name

  return `${truncate(name.split(' ')[0], 12)} ${truncate(lastName.split(' ')[0], 10)}`
}

export function truncate(str = '', maxLength = 30) {
  if (str.length > maxLength) return `${str.slice(0, maxLength + 1).trim()}...`
  return str
}

export function getCount(used, total) {
  if (!total) return 0; 
  return `${Math.min(used, total)}/${total}`
}

export function capitalize(str = '') {
  if (!str.length) return ''
  return str?.split(' ').map(s =>`${s?.charAt(0)?.toUpperCase()}${s?.substring(1)}`.trim()).join(' ')
}

export const downloadFile = ({name, url, suffix, ext = 'pdf'}) => async () => {
  const response = await fetch(url);
  const blob = await response.blob();
  
  const newUrl = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = newUrl;
  link.setAttribute('download', `${name}${suffix ? `-${suffix}` : ''}.${ext}`);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
}

export const determineStatus = ({isLoading, data, hasError}) => {
  if (isLoading) return 'loading';
  if (hasError) return 'error'
  if (!data || !data?.length) return 'empty'
  return 'loaded'
}

export function getPrice(price, currency, withCurrency, returnZero) {
  if (!price && returnZero) return '$0.00';
  if (!price) return '';
  switch(currency) {
    case 'mxn':
    case 'usd': 
      return `$${(price/100).toFixed(2)} ${withCurrency ? currency?.toUpperCase() : ''}`
    default:
      return `$${(price/100).toFixed(2)} ${currency?.toUpperCase()}`
  }
}

export function getPriceAmount(price, currency, ) {
  if (!price) return 0;
  switch(currency) {
    case 'mxn':
    case 'usd': 
      return (price/100).toFixed(2);
    default:
      return (price/100).toFixed(2);
  }
}

export const isDate = (date) => {
  return (new Date(date) !== "Invalid Date") && !isNaN(new Date(date));
}


export async function convertToBase64(file) {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();

      if (!file) return
        // Convert the file to base64 text
        reader.readAsDataURL(file);
  
        // on reader load somthing...
        reader.onload = () => {

          let fileInfo = {
              name: file.name,
              type: file.type,
              size: Math.round(file.size / 1000) + ' kB',
              base64: reader.result,
              file: file,
          }

          resolve(fileInfo)
      }
    })  
}

export function languageToCountryMapper(language) {
  if (language.includes('en')) {
    return 'us'
  }
  if (language.includes('es')) {
    return 'mx'
  }
}

export function mongoObjectId() {
  const timestamp = (new Date().getTime() / 1000 | 0).toString(16);
  return timestamp + 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, function() {
      return (Math.random() * 16 | 0).toString(16);
  }).toLowerCase();
};

export function getMongoObjectId() {
  return mongoObjectId();
};


export function checkTratoDomain(domain) {
  return ['freemium', 'starter', 'enterprise', 'pyme', 'pro', 'enterprise-test', 'enterprise-stage', 'team'].includes(domain);
}
export function checkCLMDomain(domain) {
  return ['freemium', 'enterprise', 'pyme', 'pro', 'enterprise-test', 'enterprise-stage', 'team'].includes(domain);
}

export function redirectTo(domain = '', jwt = '', username = '') {
  const isTratoDomain = checkTratoDomain(domain)
  const isCLMDomain = checkCLMDomain(domain)
  if (domain === 'compusoluciones_partner') {
    domain = 'compusoluciones'
  }
  const isInCorrectDomain = window.location.href.includes(isTratoDomain ? 'starter' : domain);
  const isDev = process.env.NODE_ENV === 'development' || window.location.href.includes('starter-test');
  // const isDev = false;
  if (!isInCorrectDomain && !isDev) {
    localStorage.removeItem('auth');
  }
  if (isCLMDomain) {
    window.location.href = `https://starter.trato.io/auth/redirect?token=${jwt}&username=${username}`
  }
  if (!isTratoDomain && !isInCorrectDomain && !isDev) {
    window.location.href = `https://${domain}.trato.io/auth/redirect?token=${jwt}&username=${username}`
  } else if (isTratoDomain && !isInCorrectDomain && !isDev) {
    // window.location.href = `https://starter.trato.io/auth/redirect?token=${jwt}&username=${username}`
    window.location.href = `https://starter.trato.io/auth/redirect?token=${jwt}&username=${username}`
  }
}

export function getFileSize(capacity) {
  const tb = capacity / 1_000_000_000_000;
  const gb = capacity / 1_000_000_000;
  const mb = capacity / 1_000_000;
  const kb = capacity / 1_000;
  let amount;
  let type;
  if (tb > 1) {
    amount = tb;
    type = 'TB';
  } else if (gb > 1) {
    amount = gb;
    type = 'GB';
  } else if (mb > 1) {
    amount = mb;
    type = 'MB';
  } else if (kb > 1) {
    amount = kb;
    type = 'KB';
  } else {
    amount = capacity;
    type = 'Bytes';
  }
  return { amount, type }
}

export const format = (date, formatStr = 'PPpp', { locale, ...options } = {}) => {
  if (!date) return '';
  return dateFnsFormat(new Date(date), formatStr, {
    locale: locales[locale ?? i18n.language.toLowerCase() ?? 'es'],
    ...options
  });
};

export const parseDate = (date, formatStr = 'PPpp') => {
  if (!date) return 'N/A';
  return dateFnsFormat(parseISO(date), formatStr, {
    locale: locales[i18n.language.toLowerCase() ?? 'es']
  });
};


export function camelize(str) {
  return accents(str)
    .replace(/[^\w\s]/gi, '')
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (letter, index) => {
      return index === 0 ? letter.toLowerCase() : letter.toUpperCase();
    })
    .replace(/\s+/g, '');
}

export function getApiName(str, index) {
  return `p${index}_${camelize(str)}`
}


export function getInitialLetters(name = '', lastName = '') {
  if (!name) return '';
  const fullName = `${name}${lastName ? ` ${lastName}` : ''}`
  let result = '';
  const splited = fullName.split(' ');
  for (let i = 0; i < (splited.length > 2 ? 2 : splited.length); i++) {
    const element = splited[i];
    result += element.substring(0, 1);
  }
  return result.toUpperCase();
};

export function cloneElementWithProps(children, componentName, props) {
  return React.Children.toArray(children).find(child => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement(child) && child?.type?.name === componentName) {
      return React.cloneElement(child, props);
    }
    return child;
  });
}
export function cloneElementsWithProps(children, props) {
  return React.Children.map(children, child => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement(child)) {
      return React.cloneElement(child, props);
    }

    return child;
  });
}

export const noop = () => {}

export function isPremiumTemplate({ canTakePremiumTier1, type, premiumTier }) {
  if (type !== 'premium') return false;
   return premiumTier === 2 || (!canTakePremiumTier1 && premiumTier === 1)
}
